/* eslint-disable @tencent/i18next/no-literal-string */
/**
 * 桌面端、移动端APP打开文档
 * 桌面端使用 PWA 加载
 * 移动端APP使用 离线包 加载
 * 分为以下几种场景：
 *
 * 完全体：3.0 已支持离线新建的 cgi
 * 1. 打开的文档是服务端已存在的文档，isNewCreate false
 * 2. 打开的文档是本地已存在的文档，但未同步服务端，isNewCreate false
 * 3. 离线新建3.0文档 isNewCreate true
 * 4. 离线打开，恢复在线
 * 5. 在线打开
 */
import set from 'lodash/set';
import { network } from '@tencent/netcheck';
import { PadInfo } from '@tencent/docs_offline';
import { DOCUMENT_STATUS } from '@tencent/ark-model';
import { IEventManager, PersistentEventTypes } from '@tencent/slide-sdk-model';
import { IMutationsPackExtended } from '@tencent/slide-sdk-persistent';
// ioc 相关逻辑
import { type ILogger, type IMqq } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IClientVars } from '@src/slide/domain/slide-app/core/interfaces/client-vars.interface';
// 当前 service 相关逻辑
import { IOfflineEditContext } from '../core/interfaces/offline-edit-context.interace';
import { IOfflineEditService } from '../core/interfaces/offline-edit-service.interface';
import { IOfflineDBService } from '../core/interfaces/offline-db-service.interface';
import {
    syncClientVars,
    syncClientVarsInfo,
    createCurClientVars,
    setClientVarsPaginated,
    createDefaultClientVars,
    fetchSlideData,
} from '../models/clientVars';
import { clientVars2OfflineData } from '../models/offlineData';
import { PadIdType, buildPadInfo, syncPadInfo as syncPadInfoFromClientVars } from '../models/padInfo';
import { OfflineEditReport, DVER, AppDocsListType } from '../constants';
import { showBlankPage, mapDocumentVersion, waitForContextDataReady } from '../utils';
import { EditEvents, IEditEvents } from '../../../core/interfaces/event.interface';
import { IOfflineMutationsService } from '../core/interfaces/offline-mutations-service.interface';
import { OfflineMutationsService } from './offline-mutations-service';
import { OnlineEditServices } from '../../online/core/online-edit-services.core';

const MODULE = '[OES]';

export class OfflineEditService implements IOfflineEditService {
    public isLocked: boolean;
    private isNewCreate: boolean;
    private curClientVars: IClientVars;
    private dbServiceLoaded = false;
    // 数据 ready 后监听并处理 edit 事件
    private OfflineMutationsService: IOfflineMutationsService;
    private useOfflineDataReady = false;
    private unCommittedOfflineData: IMutationsPackExtended[] | undefined;
    private syncDataToServerPromise: Promise<boolean> | null;

    public constructor(
        private context: IOfflineEditContext,
        private DBService: IOfflineDBService,
        private logger: ILogger,
        private mqq: IMqq,
        private event: IEditEvents,
        private sdkEvent: IEventManager,
    ) {
        this.OfflineMutationsService = new OfflineMutationsService(
            this.context,
            this.DBService,
            this.logger,
            this.sdkEvent,
            this,
        );
        this.init();
        // 绑定网络变化回调，offline不准
        network.changeEnd((status) => {
            if (status === 'online' && this.isNewCreate) {
                this.checkHandleCreateNew();
            }
        });
    }

    /**
     * 获取等待恢复的离线数据，会在DBService初始化完成后调用
     * 暴露给 SDK 使用的接口
     * @returns
     */
    public getRecoverData = () => new Promise((resolve: (data: IMutationsPackExtended[]) => void) => {
        this.waitForDBServiceLoaded(() => {
            if (this.unCommittedOfflineData) {
                return resolve(this.unCommittedOfflineData);
            }
            return this.getUnCommittedOfflineData().then(resolve);
        });
    });

    /**
     * 开始恢复离线数据，必须在外部调用，否则离线数据不会被恢复
     * 暴露给 SDK 使用的接口
     * @param recoveryCallback
     * @returns
     */
    public startRecovery = async (recoveryCallback: () => Promise<void>) => {
        if (this.unCommittedOfflineData === undefined) {
            await this.getUnCommittedOfflineData();
        }
        return this.OfflineMutationsService.applyOfflineData(this.unCommittedOfflineData ?? [], recoveryCallback);
    };

    /**
     * 退出页面时断开数据库 & 释放锁
     */
    public onBeforeUnloadCb = async () => {
        if (!this.isLocked) {
            return;
        }
        this.logger.info(MODULE, '[onBeforeUnloadCb] DBService release');
        // 断开数据库+释放互斥锁
        await this.DBService.release();
    };

    /**
     * 更新clientVars时同步title/padTitle
     * 1. 首次创建默认clientVars
     * 2. 服务端获取数据后同步clientVars
     * 3. 本地数据库获取数据后同步clientVars
     */
    public syncClientVarsToMemory = (clientVars: IClientVars): void => {
        this.curClientVars = syncClientVarsInfo(clientVars);
        syncClientVars(this.curClientVars);
        this.logger.info(MODULE, 'syncClientVarsToMemory');
        // this.logger.debug(MODULE, this.curClientVars);
    };

    /**
     * 将最终的 clientVars 数据同步至本地离线数据
     */
    public syncCurClientVarsToDB = async () => {
        const dataItem = clientVars2OfflineData(this.context, this.curClientVars);
        this.logger.info(MODULE, `ready to saveCurClientVarsToDB -
        secretId: ${this.context.secretId},
        globalPadId: ${this.context.globalPadId}`);
        // this.logger.debug(MODULE, dataItem);
        const updateResult = await this.DBService.updateDocumentData(dataItem);
        if (updateResult.error) {
            this.handleDatabaseError('saveCurClientVarsToDB', updateResult.error?.toString?.());
            return;
        }
        this.logger.info(MODULE, 'saveCurClientVarsToDB success');
        // this.logger.debug(MODULE, updateResult);
    };

    /**
     * 数据库错误统计
     * 返回是否应该重试
     * @returns
     */
    public handleDatabaseError = (stage: string, msg: string) => {
        this.logger.error(MODULE, 'handleDatabaseError', stage, msg);
        const persistent = OnlineEditServices.CollaborationManager()?.getPersistent();
        persistent && (persistent.supportOfflineEdit = false);
        if (stage === 'init') {
            this.sdkEvent.emit(PersistentEventTypes.OpenDataBaseError);
        } else {
            this.sdkEvent.emit(
                PersistentEventTypes.DataBaseFatalError,
                !!this.OfflineMutationsService.getMutationsPacks().length,
            );
        }
    };

    private getUnCommittedOfflineData = async () => {
        const unCommittedOfflineDataResult = await this.DBService.getUnCommittedOfflineData();
        if (unCommittedOfflineDataResult.error) {
            this.logger.error(MODULE, 'getUnCommittedOfflineData error: ', unCommittedOfflineDataResult.error);
            this.unCommittedOfflineData = [];
            return this.unCommittedOfflineData;
        }
        const offlineMutationsPack = unCommittedOfflineDataResult.data?.data as IMutationsPackExtended[] || [];
        this.unCommittedOfflineData = offlineMutationsPack;
        return this.unCommittedOfflineData;
    };

    private waitForDBServiceLoaded = (cb: () => void) => {
        if (this.dbServiceLoaded) {
            cb();
            return;
        }
        this.event.on(EditEvents.OfflineDBServiceReady, cb);
    };

    private async init() {
        this.logger.info(MODULE, 'init');
        this.logger.tdw?.({
            opername: 'quality_slide',
            module: 'offline-edit',
            action: 'offline-edit-error-reason',
            ver5: OfflineEditReport.LAUNCHING,
        });

        // 本地数据库初始化
        try {
            const initRes = await this.DBService.waitForDBManagerReady();
            if (!initRes) throw new Error('error');
            await this.getUnCommittedOfflineData();
            this.dbServiceLoaded = true;
            this.event.emit(EditEvents.OfflineDBServiceReady);
            // 偶现 lock 失败，不重要。不影响后续逻辑
            this.isLocked = await this.DBService.lock();
        } catch (e) {
            this.dbServiceLoaded = true;
            this.event.emit(EditEvents.OfflineDBServiceReady);
            this.handleDatabaseError('init', e?.toString());
            return;
        }
        return this.initData();
    }

    private initData() {
        this.logger.info(MODULE, `initData uid:${this.context.uid} uin:${this.context.uin} secretId:${this.context.secretId}`);
        if (this.context.isNewCreate()) {
            return this.initCreateNew();
        }
        return this.initDocMaybeExist();
    }

    private async initDocMaybeExist() {
        const info = await this.checkNewInfoInDB();
        const { isNewInDB, curPadInfo } = info;
        if (isNewInDB && curPadInfo) {
            return this.initOnlyExistLocal(curPadInfo);
        }
        return this.initAlreadyExist();
    }

    /**
     * 客户端内新建文档
     */
    private async initCreateNew() {
        this.isNewCreate = true;
        try {
            this.logger.info(MODULE, `initCreateNew
            secretId4New: ${this.context.secretId4New} -
            globalPadId4New:${this.context.globalPadId4New} -
            localPadId4New:${this.context.localPadId} -
            title4New:${this.context.title4New} -
            folder4New:${this.context.folder4New} -
            isOnlineBorrowed4New$:${this.context.isOnlineBorrowed}`);
            // 1. 新建必要参数校验
            if (!this.context.secretId4New || !this.context.globalPadId4New) {
                throw new Error(`new param invalid secretId4New: ${this.context.secretId4New}, globalPadId4New: ${this.context.globalPadId4New}`);
            }
            // 2. 创建默认 clientVars
            const defaultClientVars = await createDefaultClientVars(this.context);
            // 3. 默认数据写入本地数据库
            await this.defaultDataWriteDB(defaultClientVars);
            // 4. mqq 通知客户端 DB
            this.invokeDBOperation();
            // 5. 获取最终可用 clientVars
            const finalClientVars = await this.getFinalClientVars();
            // 6. 使用最终可用 clientVars
            await this.useFinalClientVars(finalClientVars);
            // 7. 新建成功后 emit 事件 & 日志上报
            this.afterCreateNew();
        } catch (error) {
            this.logger.error(MODULE, 'initCreateNew error: ', error);
            showBlankPage(this.context.secretId);
        }
    }

    /**
     * 打开存在于本地且尚未在服务端新建的本地新建文档
     * 主要用于兼容离线新建后列表页恢复网络未能成功同步至服务端的文档
     * 再次打开时优先拉取本地数据展示并尝试再次创建
     * @param curPadInfo
     */
    private async initOnlyExistLocal(curPadInfo: PadInfo) {
        this.isNewCreate = true;
        try {
            // 1. 通过本地数据库，更新新文档基础信息
            this.updateNewDocInfo(curPadInfo);
            this.logger.info(MODULE, `initOnlyExistLocal
            secretId4New: ${this.context.secretId4New} -
            globalPadId4New:${this.context.globalPadId4New} -
            localPadId4New:${this.context.localPadId} -
            title4New:${this.context.title4New} -
            folder4New:${this.context.folder4New} -
            isOnlineBorrowed4New$:${this.context.isOnlineBorrowed}`);
            // 2. 新建必要参数校验
            if (!this.context.secretId4New || !this.context.globalPadId4New) {
                throw new Error(`new param invalid secretId4New: ${this.context.secretId4New}, globalPadId4New: ${this.context.globalPadId4New}`);
            }
            // 3. 获取最终可用 clientVars
            const finalClientVars = await this.getFinalClientVars();
            // 4. 使用最终可用 clientVars
            this.useFinalClientVars(finalClientVars);
        } catch (error) {
            this.logger.error(MODULE, 'initOnlyExistLocal error: ', error);
            showBlankPage(this.context.secretId);
        }
    }

    /**
     * 打开已存在文档
     */
    private async initAlreadyExist() {
        this.logger.info(MODULE, 'initAlreadyExist, ready to get FinalClientVars');
        try {
            // 1. 获取最终可用 clientVars
            const finalClientVars = await this.getFinalClientVars();
            // 2. 使用最终可用 clientVars
            this.useFinalClientVars(finalClientVars);
        } catch (error) {
            this.logger.error(MODULE, 'initAlreadyExist error: ', error.toString());
            showBlankPage(this.context.secretId);
        }
        // 打开已有文档上报
        this.logger.tdw?.({
            opername: 'quality_slide',
            module: 'offline-edit',
            action: 'offline-edit-error-reason',
            ver5: this.context.isOnline() ? OfflineEditReport.USE_ONLINE : OfflineEditReport.USE_OFFLINE,
        });
    }

    private async defaultDataWriteDB(clientVars: IClientVars) {
        const offlineData = clientVars2OfflineData(this.context, clientVars, true);
        const padInfo = buildPadInfo(clientVars, this.context);
        this.logger.info(MODULE, 'ready to updateMultiTableData');
        // this.logger.debug(MODULE, padInfo);
        // 更新数据库  写入初始化clientVars & padInfo
        const writeResult = await this.DBService.updateMultiTableData(offlineData, padInfo);
        if (writeResult.error) {
            throw new Error(`clientVarsWriteDB error: ${writeResult.error}`);
        }
    }

    /**
     * 新建文档成功后通知客户端
     * @param
     * @returns
     */
    private invokeDBOperation() {
        const { isOnlineBorrowed, folder, globalPadId } = this.context;
        const docDBOperation = {
            isOnlineBorrowed: isOnlineBorrowed === 'true',
            folder,
            globalId: globalPadId,
            listTypes: [
                AppDocsListType.listTypeOffline,
                AppDocsListType.listTypeCreated,
                AppDocsListType.listTypeMyDoc,
                AppDocsListType.listTypeRecent,
            ],
            operation: 'addDocToList',
        };
        this.mqq.invoke('docx', 'docDBOperation', docDBOperation);
    }

    private getFinalClientVars(): Promise<IClientVars> {
        if (this.context.isOnline()) {
            return this.getOnlineFinalClientVars();
        }
        return this.getOfflineFinalClientVars();
    }

    private async getOnlineFinalClientVars(): Promise<IClientVars> {
        if (this.isNewCreate) {
            this.logger.info(MODULE, 'getFinalClientVars isNewCreate');
            // 同步本地数据至后台
            const isSuccess = await this.syncDataToServer();
            // 获取后台最终返回数据
            return isSuccess ? this.getClientVarsFromServer() : this.getOfflineFinalClientVars();
        }
        this.logger.info(MODULE, 'getFinalClientVars isOnlineOpen');
        return createCurClientVars();
    }

    private async getOfflineFinalClientVars() {
        this.logger.info(MODULE, 'ready to getPadInfo', this.context.secretId);
        // 从本地数据库获取最终数据
        const curPadInfo = await this.DBService.getPadInfo(this.context.secretId, PadIdType.SECRET);
        this.logger.info(MODULE, 'ready to getClientVarsFromDB');
        const clientVarsResult = await this.DBService.getClientVarsFromDB();
        if (!curPadInfo || clientVarsResult.error || !clientVarsResult.data) {
            throw new Error(`getFinalClientVars error curPadInfo: ${curPadInfo}, clientVarsResult error: ${clientVarsResult.error}, data: ${clientVarsResult.data}`);
        }
        // 历史数据兼容：数据库中存储的全量不分页数据，但是没有带 presentation
        const text = (clientVarsResult.data as IClientVars).collab_client_vars?.initialAttributedText?.text;
        if (!text?.includes('presentation')) {
            set(
                clientVarsResult.data, 'collab_client_vars.initialAttributedText.text',
                JSON.stringify({ presentation: text }),
            );
        }
        return clientVarsResult.data;
    }

    private async useFinalClientVars(finalClientVars: IClientVars) {
        // 1. 同步 clientVars
        this.syncClientVarsToMemory(finalClientVars);
        // 2. emit 数据 ready 事件
        if (this.isOfflineOrNewCreate()) this.emitDataReady();
        // 必须等待 clientVarsDataReady 事件派发完成，不然离线情况下获取的 globalPadId 是空
        await waitForContextDataReady();
        // 3. 更新全量离线数据
        const saveCurClientVars = this.syncCurClientVarsToDB();
        // 4. 更新 padInfo 数据
        const updateCurPadInfo = this.updateCurPadInfo();
        // 5. 应用离线数据
        await Promise.all([saveCurClientVars, updateCurPadInfo]);
        // 6. 通知客户端当前文档版本号
        this.invokeMapDocumentVersion();
    }

    /**
     * 获取当前文档PadInfo信息
     * 主要用于区分是否是存在于本地DB且尚未同步服务端的新建文档
     * @returns
     */
    private checkNewInfoInDB = async () => {
        this.logger.info(MODULE, 'ready to getPadInfo', this.context.secretId);
        const curPadInfo = await this.DBService.getPadInfo(this.context.secretId, PadIdType.SECRET);
        const isNewInDB = (curPadInfo?.status as any) === DOCUMENT_STATUS.new;
        return {
            isNewInDB,
            curPadInfo,
        };
    };

    /**
     * 从服务器拉取 clientVars
     */
    private async getClientVarsFromServer() {
        try {
            this.logger.info(MODULE, 'ready to fetchSlideData', this.context.secretId);
            const slideDataResult = await fetchSlideData(this.context.secretId);
            const { data } = slideDataResult.data;
            if (!data) throw new Error('fetchSlideData error');
            return data;
        } catch (err) {
            return null;
        }
    }

    /**
     * 新建/离线打开文档 需主动 emit clientVarsDataReady (在线打开已有文档会在拉取到数据时派发事件)
     * 因初始化获取的是全量数据需要重置分页请求配置
     * 确保 ioc client-vars-loader 可以正常 resolve
     */
    private emitDataReady(): void {
        // 主动 emit 的 clientVars 数据 均为带 presentation 的全量数据，需 mock 不分页参数
        this.curClientVars = setClientVarsPaginated(this.curClientVars);
        this.logger.info(MODULE, 'dispatch clientVarsDataReady');
        dispatchEvent(new Event('clientVarsDataReady'));
        syncClientVars(this.curClientVars);
    }

    /**
     * 更新当前文档padInfo
     */
    private updateCurPadInfo = async () => {
        const padInfoFromDB = await this.DBService.getPadInfo(this.context.globalPadId);
        // 本地已有 padInfo 则更新字段，没有则新建 padInfo
        const syncPadInfo = padInfoFromDB
            ? syncPadInfoFromClientVars(padInfoFromDB, this.curClientVars)
            : buildPadInfo(this.curClientVars, this.context);
        const padInfoData = {
            padInfo: syncPadInfo,
            isAlreadyExist: !!padInfoFromDB,
        };
        this.logger.info(MODULE, `ready to updateCurPadInfo -
            secretId: ${this.context.secretId},
            globalPadId: ${this.context.globalPadId}`);
        // this.logger.debug(MODULE, 'padInfoData:', padInfoData);
        // 更新 padInfo
        const updateResult = await this.DBService.updatePadInfo(padInfoData);
        if (updateResult.error) {
            this.logger.info(MODULE, 'updateCurPadInfo 更新 padInfo 失败 error: ', updateResult.error);
        }
        return updateResult;
    };

    // 更新成功则通知客户端版本映射关系
    private invokeMapDocumentVersion() {
        const dver = this.curClientVars.collab_client_vars?.dver || DVER;
        this.logger.info(MODULE, `updateCurPadInfo mapDocumentVersion secretId: ${this.context.secretId}, dver: ${dver}`);
        mapDocumentVersion(this.context.secretId, dver);
    }

    private afterCreateNew() {
        window.dispatchEvent(new CustomEvent('CreateOfflineDoc', {
            detail: {
                title: this.curClientVars.title,
            },
        }));
        this.logger.tdw?.({
            opername: 'quality_slide',
            module: 'offline-edit',
            action: 'offline-edit-error-reason',
            ver5: OfflineEditReport.USE_OFFLINE_CREATE,
        });
    }

    private updateNewDocInfo(curPadInfo: PadInfo) {
        this.context.updateCreateDocInfo({
            secretId: curPadInfo.secretId,
            globalPadId: curPadInfo.globalPadId,
            title: curPadInfo.title,
            folder: curPadInfo.folder,
        });
    }

    /**
     * 同步本地新建文档到后台
     * @param secretId secretId
     * @param globalPadId globalPadId
     * @param folderId folderId
     */
    private syncDataToServer = async (): Promise<boolean> => {
        if (!this.syncDataToServerPromise) {
            this.syncDataToServerPromise = this.handleSyncDataToServer();
        }
        return this.syncDataToServerPromise;
    };

    // eslint-disable-next-line no-async-promise-executor
    private handleSyncDataToServer = (): Promise<boolean> => new Promise(async (resolve) => {
        this.logger.info(MODULE, 'ready to syncDataToServer');
        const syncResult = await this.DBService.createFile();
        if (syncResult.error) {
            this.logger.info(MODULE, 'syncData error', syncResult);
            resolve(false);
        } else {
            this.logger.info(MODULE, 'syncData success');
            // this.logger.debug(MODULE, 'syncData success', syncResult);
            // 同步成功后删除掉离线新建信息
            window.history.replaceState(null, '', location.href.substring(0, location.href.indexOf('?')));
            // 创建接口会更新数据库中文档状态，此处需更新内存中 clientVars
            this.syncClientVarsToMemory(syncResult?.data as IClientVars);
            // 文档创建后，打开协作
            OnlineEditServices.CollaborationManager.promise().then((col) => {
                const socket = col.getSocket();
                socket.reInit({
                    ...socket.getSocketConfig(),
                    serverVars: this.curClientVars.collab_client_vars as any,
                });
                socket.open();
            });
            resolve(true);
        }
        this.syncDataToServerPromise = null;
    });

    // 离转在检查是否需要创建文档
    private checkHandleCreateNew = async () => {
        this.logger.info(MODULE, 'checkHandleCreateNew');
        const curPadInfo = await this.DBService.getPadInfo(this.context.secretId, PadIdType.SECRET);
        // 新建且尚未同步后台的文档需要同步后台
        if ((curPadInfo?.status as any) === DOCUMENT_STATUS.new) {
            this.logger.info(MODULE, 'checkAndSubmit isOnline and status is new');
            await this.syncDataToServer();
        }
    };

    private isOfflineOrNewCreate = () => this.isNewCreate || !this.context.isOnline();
}
